import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Turnstile } from '@marsidev/react-turnstile';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { socket } from '../services/socket';
import { useStore } from '../store';
import LanguageSwitcher from '../components/LanguageSwitcher';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://app.tahc.ai';

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setUser = useStore((state) => state.setUser);
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite_token');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileLoaded, setTurnstileLoaded] = useState(false);

  useEffect(() => {
    document.title = 'Login - tahc';
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Only require Turnstile if it's configured and loaded
    if (import.meta.env.VITE_TURNSTILE_SITE_KEY && turnstileLoaded && !turnstileToken) {
      setError('Please complete the security check.');
      return;
    }

    setLoading(true);

    try {
      const { user, tokens } = await api.login({ ...formData, turnstileToken: turnstileToken || undefined });
      setUser(user);
      socket.connect(tokens.accessToken);

      // If there's an invite token, automatically accept it
      if (inviteToken) {
        try {
          const res = await fetch(`${API_URL}/api/team/accept-invite`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${tokens.accessToken}`,
            },
            body: JSON.stringify({
              token: inviteToken,
              userId: user.id,
            }),
          });

          const data = await res.json();

          if (data.success) {
            // Update user's organization in store
            user.organization_id = data.organization_id;
            setUser(user);
          } else {
            console.error('Failed to accept invite:', data.error);
            setError(data.error || 'Failed to accept invitation. Logging you in anyway...');
            // Continue to dashboard even if invite acceptance fails
            setTimeout(() => navigate('/dashboard'), 2000);
            return;
          }
        } catch (inviteErr) {
          console.error('Error accepting invite:', inviteErr);
          // Continue to dashboard even if invite acceptance fails
        }
      }

      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex justify-center p-4 py-8 overflow-y-auto">
      <div className="max-w-md w-full my-auto">
        <div className="bg-white rounded-3xl shadow-2xl p-4 sm:p-8 border-2 border-gray-200">
          {/* Language Switcher */}
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>

          <div className="text-center mb-4 sm:mb-8">
            <img src="/tahc-logo-2026.jpg" alt="tahc" className="w-16 h-16 sm:w-40 sm:h-40 mx-auto mb-2 sm:mb-6 object-contain" />
            <p className="text-gray-600 text-xs sm:text-base">{t('auth.signInSubtitle')}</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                {t('auth.email')}
              </label>
              <input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-gray-900 focus:border-transparent transition"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  {t('auth.password')}
                </label>
                <Link to="/forgot-password" className="text-sm text-gray-600 hover:text-gray-900">
                  {t('auth.forgotPassword')}
                </Link>
              </div>
              <input
                id="password"
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-gray-900 focus:border-transparent transition"
                placeholder="••••••••"
              />
            </div>

            {import.meta.env.VITE_TURNSTILE_SITE_KEY && (
              <div className="flex justify-center">
                <style>{`
                  .turnstile-wrapper {
                    position: relative;
                    border-radius: 12px;
                    overflow: hidden;
                    width: 300px;
                    height: 65px;
                  }

                  /* Force all Turnstile elements to have rounded corners */
                  .turnstile-wrapper * {
                    border-radius: 12px !important;
                  }

                  /* Clip the iframe content with rounded corners */
                  .turnstile-wrapper::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    border: 2px solid transparent;
                    border-radius: 12px;
                    pointer-events: none;
                    z-index: 10;
                  }
                `}</style>
                <div className="turnstile-wrapper">
                  <Turnstile
                    siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                    onSuccess={(token) => {
                      setTurnstileToken(token);
                      setTurnstileLoaded(true);
                    }}
                    onError={() => {
                      setTurnstileToken(null);
                      setTurnstileLoaded(false);
                    }}
                    onExpire={() => {
                      setTurnstileToken(null);
                    }}
                  />
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || Boolean(import.meta.env.VITE_TURNSTILE_SITE_KEY && turnstileLoaded && !turnstileToken)}
              className="w-full bg-black text-white py-3 rounded-full font-medium hover:bg-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? `${t('common.loading')}` : t('auth.login')}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-gray-600">
            {t('auth.dontHaveAccount')}{' '}
            <Link to="/register" className="text-gray-900 hover:text-gray-700 font-medium underline">
              {t('auth.signUpHere')}
            </Link>
          </div>

          <div className="mt-4 text-center text-xs text-gray-500">
            <a href="https://tahc.ai" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700">
              {t('register.learnMore')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
